import React, {
  forwardRef,
  ReactNode,
  Suspense,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import {
  AssetManagerProvider,
  useAssetManager,
} from "@/remotion/core/AssetManager";
import { ReadOnlyCardObjects } from "@/remotion/core/derivedSchema";
import { loadWrapVideoData } from "@/remotion/core/loader";
import {
  useWrapVideoSnap,
  WrapVideoContextProvider,
} from "@/remotion/editor/WrapVideoContextProvider";
import { RemotionPlayerPreview } from "@/remotion/Player";
import { useS3Object } from "@/services/Amplify/hooks/getS3Object";
import { useImageRetry } from "@/services/Amplify/hooks/useImageRetry";
import { trpc, type RouterOutput } from "@/services/Trpc/client";
import { ErrorBoundary } from "@/utils/ErrorBoundary";
import DefaultCardImg from "public/images/defaultcard.png";
import { twMerge } from "tailwind-merge";
import { useDebounceValue } from "usehooks-ts";
import { useSnapshot } from "valtio/react";
import { useInView } from "react-intersection-observer";

import { useUserIsSubscribed } from "@/utils/auth/useUserIsSubscribed";

import { useHomePageModalContext } from "./HomePageModal";
import { useTemplateListContext } from "./TemplateList";

// Lock icon component for Premium templates (decorative - text overlay provides context)
const LockIcon = () => (
  <svg
    data-testid="lock-icon"
    aria-hidden="true"
    className="h-3 w-3 ml-1"
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path
      fillRule="evenodd"
      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
      clipRule="evenodd"
    />
  </svg>
);

// CardTemplateVideoPreview - displays the video player with blurred background
// Buttons are rendered at TemplateListItem level, not here
const CardTemplateVideoPreview = ({
  cardObjects,
  thumbnailKey,
}: {
  cardObjects: ReadOnlyCardObjects;
  thumbnailKey: string;
}) => {
  const wrapVideo = useWrapVideoSnap();
  const ready = useSnapshot(useAssetManager()).loaded;

  // This ensures that the thumbnail is displayed on top of the player for 50 extra ms
  // this gives the player extra time to render itself
  // this prevents showing a frame of white while the browser renders the player
  const [showVideo, setShowVideo] = useDebounceValue(false, 50);
  useLayoutEffect(() => setShowVideo(ready), [ready]);

  // show the layout spinner after loading for 500ms
  const timeout = useRef<NodeJS.Timeout | undefined>();
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    timeout.current = setTimeout(() => setShowSpinner(true), 500);
    return () => clearTimeout(timeout.current);
  }, []);

  if (!ready) {
    const className = twMerge(
      "h-full w-full",
      showSpinner ? "hover:cursor-progress" : "",
    );
    return (
      <div className={className}>
        <CardTemplateImagePreview imgKey={thumbnailKey} disableDownload />
      </div>
    );
  }

  // Sandwich layer architecture to prevent blinking:
  // - Bottom layer (z-1): Blurred thumbnail background (always visible, fills white space)
  // - Middle layer (z-10): Video player (pointer-events: none)
  // - Buttons are rendered at TemplateListItem level (z-50) to be always on top
  return (
    <>
      {/* BOTTOM LAYER: Blurred thumbnail background - always visible to fill white space around video */}
      <div
        className="absolute h-full w-full overflow-hidden rounded-2xl"
        style={{
          zIndex: 1,
          pointerEvents: "none", // No interactions on background layer
        }}
      >
        <CardTemplateImagePreview
          imgKey={thumbnailKey}
          blurOnly={true} // Only show blurred background
          disableDownload
        />
      </div>

      {/* MIDDLE LAYER: Video player - visible when ready, but doesn't capture events */}
      <div
        className="absolute flex items-center justify-center h-full w-full"
        style={{
          zIndex: 10,
          pointerEvents: "none", // Allow clicks to pass through
          opacity: showVideo ? 1 : 0, // Fade in when ready
          transition: "opacity 50ms",
        }}
      >
        <RemotionPlayerPreview
          style={{ width: "100%", maxHeight: "100%" }}
          controls={false}
          loop
          autoPlay
          wrapVideo={wrapVideo}
          cardObjects={cardObjects}
          disableDownload
        />
      </div>
    </>
  );
};

// Play button overlay for thumbnail state
// Rendered at TemplateListItem level with z-40 to be above sandwich layers (z-1, z-10, z-20)
// and Free badge (z-30), but below modals (z-50)
// Container is pointer-events-none to not interfere with scroll, button is pointer-events-auto
const PlayButtonOverlay = ({ onPlay }: { onPlay: () => void }) => (
  <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none">
    <button
      aria-label="Play preview"
      onPointerDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        // console.log('▶️ Play button clicked');
        onPlay();
      }}
      className="pointer-events-auto flex h-16 w-16 items-center justify-center rounded-full bg-purple-600/30 backdrop-blur-sm transition-all duration-200 hover:bg-purple-600/50 hover:scale-110 active:scale-95"
    >
      <svg
        className="h-8 w-8 text-white/80 ml-1"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M8 5v14l11-7z" />
      </svg>
    </button>
  </div>
);

// Customize button overlay for video playing state
// Rendered at TemplateListItem level with z-40 to be above sandwich layers (z-1, z-10, z-20)
// and Free badge (z-30), but below modals (z-50)
const CustomizeButtonOverlay = ({ onClick }: { onClick: () => void }) => (
  <div className="absolute bottom-3 right-3 z-40 pointer-events-none">
    <button
      aria-label="Customize this card"
      onPointerDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        // console.log('🎨 Customize button clicked');
        onClick();
      }}
      className="pointer-events-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-600/80 backdrop-blur-sm text-white text-xs font-medium shadow-lg transition-all duration-200 hover:bg-purple-600 hover:scale-105 active:scale-95"
    >
      <svg
        className="h-3.5 w-3.5 text-white/90"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        {/* Pencil/edit icon */}
        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
      </svg>
      Customize
    </button>
  </div>
);

// CardTemplateImagePreview - displays the thumbnail image
// Buttons are rendered at TemplateListItem level, not here
export const CardTemplateImagePreview: React.FC<{
  imgKey: string;
  blurOnly?: boolean; // If true, only show blurred background (for video backdrop)
  disableDownload?: boolean; // Prevent right-click, drag, and selection for IP protection
}> = ({ imgKey, blurOnly = false, disableDownload = false }) => {
  const { data, refetch } = useS3Object({ key: imgKey });
  const { src, handleError } = useImageRetry({
    data,
    refetch,
    fallbackSrc: DefaultCardImg.src,
    resetKey: imgKey,
  });

  return (
    <div
      className={`duration-400 relative h-full max-h-full scale-100 overflow-hidden${disableDownload ? " select-none" : ""}`}
      onContextMenu={disableDownload ? (e) => e.preventDefault() : undefined}
      onDragStart={disableDownload ? (e) => e.preventDefault() : undefined}
    >
      {/* Blurred background - always visible.
          `unoptimized`: these are user-uploaded S3 thumbnails behind a
          signed URL. Routing them through the Next.js image optimizer means
          a stale/orphaned key (old cards) makes the optimizer server-fetch a
          403/404 and return an HTTP 500 on /_next/image. Bypassing the
          optimizer makes a broken thumbnail fail as an ordinary <img> error
          → onError → fallback card, with no 500. */}
      <Image
        src={src}
        onError={handleError}
        className="-z-0 object-cover blur-lg"
        alt=""
        fill
        sizes="50vw"
        unoptimized
        draggable={disableDownload ? false : undefined}
      />
      {/* Sharp image on top - hidden when blurOnly mode (used as video background) */}
      {!blurOnly && (
        <Image
          src={src}
          onError={handleError}
          className="absolute z-10 object-contain"
          alt=""
          fill
          sizes="50vw"
          unoptimized
          draggable={disableDownload ? false : undefined}
        />
      )}
    </div>
  );
};

// CardTemplatePreview - handles showing either image thumbnail or video preview
// Buttons are now rendered at TemplateListItem level, not here
const CardTemplatePreview = ({
  template,
  enablePreview,
}: {
  template: CardTemplate;
  enablePreview: boolean;
}) => {
  const { data, isLoading, isError } = trpc.cardTemplate.getWithVideo.useQuery(
    {
      id: template.id,
    },
    {
      enabled: enablePreview,
      staleTime: Infinity,
    },
  );

  const imagePreview = useMemo(
    () => (
      <CardTemplateImagePreview
        imgKey={template.thumbnailURL}
        disableDownload
      />
    ),
    [template.thumbnailURL],
  );

  if (enablePreview && data) {
    const cardObjects = loadWrapVideoData(
      data.wrapVideo.cardObjects as unknown[],
    );

    return (
      <ErrorBoundary>
        <AssetManagerProvider cardObjects={cardObjects} prefetch>
          <WrapVideoContextProvider wrapVideo={data.wrapVideo}>
            <CardTemplateVideoPreview
              cardObjects={cardObjects}
              thumbnailKey={template.thumbnailURL}
            />
          </WrapVideoContextProvider>
        </AssetManagerProvider>
      </ErrorBoundary>
    );
  }

  return imagePreview;
};

type CardTemplate = RouterOutput["cardTemplate"]["list"]["items"][number];

const TemplateListItem = ({
  template,
  isBlankListing,
}: {
  template: CardTemplate;
  isBlankListing?: boolean;
}) => {
  const isSubscribed = useUserIsSubscribed();
  const showHomePageModal = useHomePageModalContext((ctx) => ctx.show);

  const showHomePageModalTemplate = useHomePageModalContext(
    (ctx) => ctx.setTemplate,
  );

  const openDialog = () => {
    showHomePageModal();
    showHomePageModalTemplate(template);
  };
  const { liveVideoPreviewData, setLiveVideoPreviewData, isSingleColumn } =
    useTemplateListContext();

  const enablePreview =
    liveVideoPreviewData?.id === template.id &&
    liveVideoPreviewData?.blank === !!isBlankListing;

  // Track visibility for auto-play on scroll
  // Only enabled in single column mode to avoid race conditions with hover in multi-column
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.5, // Trigger when 50% visible
    skip: !isSingleColumn, // Only observe in single column mode
    initialInView: true, // Detect items already in view when observer becomes active (fixes first video issue)
  });

  // HOT POTATO METHOD: In single column mode with auto-scroll, cards pass preview like a hot potato
  // When scrolled into view → grab the potato (claim preview, may steal from another card)
  // When scrolled out of view → drop the potato (release preview if holding it)
  // Only reacts to THIS card's inView changes, preventing infinite loops
  useEffect(() => {
    if (!isSingleColumn) return; // Only auto-play on scroll in single column mode

    if (inView) {
      // Grab the hot potato! This card wants the preview
      // console.log('🥔 HOT POTATO: Grabbed by', template.name, `(${template.id})`);
      setLiveVideoPreviewData({ id: template.id, blank: !!isBlankListing });
    } else if (enablePreview) {
      // Drop the hot potato! This card is leaving, release preview only if we have it
      // console.log('🥔 HOT POTATO: Dropped by', template.name, `(${template.id})`);
      setLiveVideoPreviewData(undefined);
    }
    // ONLY react to inView changes for THIS card (scroll events)
    // Don't include enablePreview - that would cause infinite loops when cards compete
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSingleColumn, inView, template.id, isBlankListing]);

  // TEMPORARILY DISABLED: Touch event handling for tap-to-preview and long-press-to-customize
  // Commented out to isolate button functionality issues
  // TODO: Re-enable after buttons are working correctly
  /*
  const longPressTimerRef = useRef<NodeJS.Timeout | undefined>();
  const [isLongPress, setIsLongPress] = useState(false);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    // Record start position to detect scrolling
    const touch = e.touches[0];
    if (touch) {
      touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    }
    isTouchInteraction.current = true; // Flag this as a touch interaction
    setIsLongPress(false);
    // Start long press timer (500ms)
    longPressTimerRef.current = setTimeout(() => {
      setIsLongPress(true);
      openDialog(); // Long press → open customization modal
    }, 500);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Clear long press timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    // Check if user was scrolling (moved more than 10px)
    const touch = e.changedTouches[0];
    let wasScrolling = false;
    if (touch && touchStartPos.current) {
      const dx = Math.abs(touch.clientX - touchStartPos.current.x);
      const dy = Math.abs(touch.clientY - touchStartPos.current.y);
      wasScrolling = dx > 10 || dy > 10;
    }
    touchStartPos.current = null;

    // If not a long press AND not scrolling, it's a short tap → grab hot potato
    if (!isLongPress && !wasScrolling) {
      console.log('🥔 HOT POTATO: Grabbed by TAP on', template.name, `(${template.id})`);
      setLiveVideoPreviewData({ id: template.id, blank: !!isBlankListing });
    }

    // Reset touch flag after a longer delay to block simulated mouse events
    setTimeout(() => {
      isTouchInteraction.current = false;
    }, 500);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // If user moves finger, cancel long press (they're scrolling)
    const touch = e.touches[0];
    if (touch && touchStartPos.current) {
      const dx = Math.abs(touch.clientX - touchStartPos.current.x);
      const dy = Math.abs(touch.clientY - touchStartPos.current.y);
      if (dx > 10 || dy > 10) {
        // User is scrolling, cancel long press timer
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = undefined;
        }
      }
    }
  };

  const handleTouchCancel = () => {
    // Clean up if touch is cancelled
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
    setIsLongPress(false);
    isTouchInteraction.current = false;
    touchStartPos.current = null;
  };
  */

  // Simple click handler - opens dialog on click (for clicking on card background)
  const handleClick = () => {
    openDialog();
  };

  // TEMPORARILY DISABLED: Hover handlers commented out to isolate button issues
  // Will re-enable after buttons are working consistently
  /*
  // Mouse hover handling for desktop (pointer devices only, not mobile)
  const handleMouseEnter = () => {
    console.log('🔍 handleMouseEnter:', { isSingleColumn, enablePreview, templateName: template.name });
    if (isSingleColumn) return; // Mobile uses touch/scroll, not hover
    if (enablePreview) return; // Already previewing this card
    console.log('🥔 HOT POTATO: Grabbed by HOVER on', template.name, `(${template.id})`);
    setLiveVideoPreviewData({ id: template.id, blank: !!isBlankListing });
  };

  const handleMouseLeave = () => {
    if (isSingleColumn) return; // Mobile uses touch/scroll, not hover
    console.log('🥔 HOT POTATO: Dropped by HOVER LEAVE on', template.name, `(${template.id})`);
    setLiveVideoPreviewData(undefined);
  };
  */

  // Handler for play button - grabs hot potato
  const handlePlayClick = () => {
    // console.log('🥔 HOT POTATO: Grabbed by PLAY BUTTON on', template.name, `(${template.id})`);
    setLiveVideoPreviewData({ id: template.id, blank: !!isBlankListing });
  };

  return (
    <div
      ref={inViewRef}
      className="group relative flex justify-center overflow-visible ring-blue-500 hover:cursor-pointer"
      onClick={handleClick} // Mouse click on card background → open customization modal
      // TEMPORARILY DISABLED: Hover and touch handlers commented out to isolate button issues
      // onMouseEnter={handleMouseEnter}
      // onMouseLeave={handleMouseLeave}
      // onTouchStart={handleTouchStart}
      // onTouchMove={handleTouchMove}
      // onTouchEnd={handleTouchEnd}
      // onTouchCancel={handleTouchCancel}
    >
      {/* Content container - overflow-hidden for rounded corners */}
      <div className="aspect-9/16 group flex max-h-[70vh] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-md ring-white ring-offset-1 hover:shadow-lg hover:shadow-white hover:ring-4">
        <CardTemplatePreview
          template={template}
          enablePreview={enablePreview}
        />

        <div className="absolute bottom-2 left-2 mr-2 font-medium opacity-100 transition-opacity group-hover:opacity-0">
          <div className="rounded-xl bg-white/60 p-1 px-2">{template.name}</div>
        </div>
        {/* Template Tier Badge */}
        {template.isFree ? (
          // FREE Badge - Green, available to all users
          <div
            className="hover:inset right-[-3%] top-4 flex items-center justify-center bg-green-600 px-[1.9rem] pb-1 font-semibold text-white group-hover:right-[-5%]"
            style={{
              position: "absolute",
              height: 40,
              zIndex: 30, // Above sandwich layers (z-1, z-10, z-20)
              boxShadow: "0 -10px 0 inset #0005",
              clipPath:
                "polygon(0 0, 100% 0, 100% 90%, 90% 100%, 90% 90%, 0 90%, 10px 45%",
              transition: "right 500ms",
            }}
          >
            Free
          </div>
        ) : (
          // PREMIUM Badge - Violet, with lock for non-subscribers
          <div
            className="hover:inset right-[-3%] top-4 flex items-center justify-center bg-violet-600 px-[1.4rem] pb-1 font-semibold text-white group-hover:right-[-5%]"
            style={{
              position: "absolute",
              height: 40,
              zIndex: 30, // Above sandwich layers (z-1, z-10, z-20)
              boxShadow: "0 -10px 0 inset #0005",
              clipPath:
                "polygon(0 0, 100% 0, 100% 90%, 90% 100%, 90% 90%, 0 90%, 10px 45%",
              transition: "right 500ms",
            }}
          >
            Premium
            {!isSubscribed && <LockIcon />}
          </div>
        )}

        {/* Members Only Overlay - shown for premium templates to non-subscribers */}
        {!template.isFree && !isSubscribed && (
          <div
            className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs py-1.5 text-center font-medium"
            style={{ zIndex: 25 }}
          >
            Members only
          </div>
        )}
      </div>

      {/* BUTTON OVERLAYS - Rendered at TemplateListItem level, OUTSIDE overflow-hidden container */}
      {/* z-40 ensures they're above sandwich layers and Free badge, but below modals (z-50) */}
      {!enablePreview && <PlayButtonOverlay onPlay={handlePlayClick} />}
      {enablePreview && <CustomizeButtonOverlay onClick={openDialog} />}
    </div>
  );
};

export const TemplateListSkeleton = forwardRef<HTMLDivElement>((_, ref) => {
  return (
    <div
      ref={ref}
      className="aspect-9/16 relative  max-h-[70vh] w-full animate-pulse rounded-2xl bg-gray-300  shadow-md ring-white ring-offset-1"
    >
      <div className="absolute  bottom-2 left-2 h-[2rem] w-32 rounded-xl bg-gray-400 "></div>
    </div>
  );
});
TemplateListSkeleton.displayName = "TemplateListSkeleton";

export default TemplateListItem;
